<script>
  import { routeStore, currentStop, completedCount, totalStops, remainingStops, totalDistance, totalTime } from '../lib/stores/routeStore.js';
  import Button from '../lib/components/Button.svelte';
  import Card from '../lib/components/Card.svelte';

  export let onNewRoute;
  export let onBackToHome;

  $: route = $routeStore;
  $: current = $currentStop;
  $: completed = route.completedStops;
  $: remaining = route.optimizedSequence.slice(route.currentStopIndex + 1);
  $: isComplete = route.currentStopIndex >= route.optimizedSequence.length;

  function handleNavigate(postcode) {
    let origin;

    // Determine origin: last completed stop, or starting location if none completed
    if (completed.length > 0) {
      // Use the last completed stop as origin
      const lastCompleted = completed[completed.length - 1];
      origin = lastCompleted.postcode;
    } else {
      // Use starting location
      if (route.startLocation.postcode) {
        origin = route.startLocation.postcode;
      } else if (route.startLocation.lat && route.startLocation.lng) {
        // GPS coordinates
        origin = `${route.startLocation.lat},${route.startLocation.lng}`;
      }
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(postcode)}`;
    window.open(url, '_blank');
  }

  function handleViewFullRoute() {
    // Build origin
    let origin;
    if (route.startLocation.postcode) {
      origin = route.startLocation.postcode;
    } else if (route.startLocation.lat && route.startLocation.lng) {
      origin = `${route.startLocation.lat},${route.startLocation.lng}`;
    }

    // Build waypoints from optimized sequence (max 9 waypoints for Google Maps)
    const allStops = route.optimizedSequence.map(idx => route.postcodes[idx]);
    let waypoints, destination;

    if (route.endLocation) {
      // If end location is set, use it as destination
      if (route.endLocation.postcode) {
        destination = route.endLocation.postcode;
      } else if (route.endLocation.lat && route.endLocation.lng) {
        destination = `${route.endLocation.lat},${route.endLocation.lng}`;
      }
      // All delivery stops become waypoints (limit to first 9)
      waypoints = allStops.slice(0, 9).join('|');
    } else {
      // No end location, last delivery stop is destination
      destination = allStops[allStops.length - 1];
      // All but last stop are waypoints (limit to first 9)
      waypoints = allStops.slice(0, Math.min(9, allStops.length - 1)).join('|');
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&waypoints=${encodeURIComponent(waypoints)}`;
    window.open(url, '_blank');
  }

  function handleMarkComplete() {
    routeStore.markComplete();
  }

  function handlePrioritize(index) {
    routeStore.prioritizePostcode(index);
  }

  function handleNavigateToEnd() {
    // Get origin from last completed stop
    const lastCompleted = completed[completed.length - 1];
    const origin = lastCompleted.postcode;

    // Get destination from end location
    let destination;
    if (route.endLocation.postcode) {
      destination = route.endLocation.postcode;
    } else if (route.endLocation.lat && route.endLocation.lng) {
      destination = `${route.endLocation.lat},${route.endLocation.lng}`;
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;
    window.open(url, '_blank');
  }

  function handleExportCSV() {
    // Generate CSV
    const headers = ['Sequence', 'Postcode', 'Status', 'Distance (km)', 'Time (min)', 'Completed At'];
    const rows = [];

    // Add completed stops
    completed.forEach(stop => {
      rows.push([
        stop.sequence,
        stop.postcode,
        'Completed',
        stop.distanceKm.toFixed(2),
        Math.round(stop.timeMin),
        new Date(stop.completedAt).toLocaleString()
      ]);
    });

    // Add current stop if not complete
    if (current && !isComplete) {
      rows.push([
        current.sequence,
        current.postcode,
        'Current',
        '-',
        '-',
        '-'
      ]);
    }

    // Add remaining stops
    remaining.forEach((postcodeIndex, i) => {
      rows.push([
        route.currentStopIndex + i + 2,
        route.postcodes[postcodeIndex],
        'Pending',
        '-',
        '-',
        '-'
      ]);
    });

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `med-map-route-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function formatTime(minutes) {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  }
</script>

<div class="min-h-screen" style="background: var(--bg);">
  <!-- Header with mearsit logo -->
  <header class="sticky top-0 z-50 backdrop-blur-md" style="background: rgba(250, 248, 243, 0.88); border-bottom: 1px solid var(--border);">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <button on:click={onBackToHome} class="flex items-center gap-4 hover:opacity-80 transition-opacity" style="background: none; border: none; cursor: pointer; padding: 0;">
          <img src="/mearsit-lockup.png" alt="mearsit — software · analysis · delivery" class="h-10" style="display: block;" />
        </button>
        <p style="font-size: 1rem; font-weight: 600; color: var(--fg); font-family: var(--font-display);">ScriptRunner</p>
      </div>
    </div>
  </header>

  <div class="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between animate-fade-in">
      <div>
        <h1 class="text-2xl sm:text-3xl font-bold" style="font-family: var(--font-display); color: var(--fg);">
          {#if isComplete}
            Route Complete!
          {:else}
            Your Route
          {/if}
        </h1>
        <p class="text-sm mt-1" style="color: var(--fg-muted);">
          {$completedCount} of {$totalStops} deliveries completed
        </p>
      </div>

      <div class="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          on:click={handleViewFullRoute}
        >
          View Full Route
        </Button>
        <div class="text-right">
          <div class="text-2xl font-bold" style="color: var(--accent);">
            {($totalDistance).toFixed(1)}<span class="text-sm ml-1" style="color: var(--fg-subtle);">km</span>
          </div>
          <div class="text-xs" style="color: var(--fg-subtle);">
            ~{formatTime($totalTime)}
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="w-full rounded-full h-3 overflow-hidden" style="background: var(--bg-sunken); box-shadow: var(--shadow-sm);">
      <div
        class="h-full rounded-full transition-all duration-500 ease-out"
        style="width: {($completedCount / $totalStops) * 100}%; background: linear-gradient(to right, var(--accent), var(--fern-300));"
      ></div>
    </div>

    <!-- Completed Stops -->
    {#if completed.length > 0}
      <div class="animate-slide-up">
        <div class="mb-3 flex items-center gap-2 text-sm" style="color: var(--fg-subtle);">
          <span>✓</span>
          <span>Completed</span>
        </div>

        <div class="space-y-2">
          {#each completed as stop}
            <Card padding="sm">
              <div class="flex items-center justify-between opacity-60">
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold flex-shrink-0" style="background: var(--fern-300); color: var(--paper-0);">
                    ✓
                  </div>
                  <div>
                    <div class="font-mono font-semibold text-sm" style="color: var(--fg);">
                      {stop.postcode}
                    </div>
                    <div class="text-xs" style="color: var(--fg-subtle);">
                      {new Date(stop.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {#if stop.distanceKm > 0}
                        • {stop.distanceKm.toFixed(1)} km • {Math.round(stop.timeMin)} min
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Current Stop -->
    {#if current && !isComplete}
      <Card padding="lg" elevated={true}>
        <div class="text-center space-y-6">
          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-4" style="background: rgba(30, 90, 126, 0.1); color: var(--accent);">
              <span class="w-2 h-2 rounded-full animate-pulse" style="background: var(--accent);"></span>
              <span>Next Stop</span>
            </div>

            <div class="font-mono text-4xl sm:text-5xl font-bold mb-2" style="color: var(--fg);">
              {current.postcode}
            </div>

            <div style="color: var(--fg-muted);">
              Stop {current.sequence} of {$totalStops}
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 max-w-xs mx-auto">
            <Button
              variant="primary"
              size="lg"
              fullWidth={true}
              on:click={() => handleNavigate(current.postcode)}
            >
              <span class="flex flex-col items-center gap-1">
                <span class="text-xl">🗺️</span>
                <span class="text-sm">Navigate</span>
              </span>
            </Button>

            <Button
              variant="success"
              size="lg"
              fullWidth={true}
              on:click={handleMarkComplete}
            >
              <span class="flex flex-col items-center gap-1">
                <span class="text-xl">✓</span>
                <span class="text-sm">Complete</span>
              </span>
            </Button>
          </div>
        </div>
      </Card>
    {/if}

    <!-- Completion Message -->
    {#if isComplete}
      <Card padding="lg" elevated={true}>
        <div class="text-center space-y-4">
          <div class="text-6xl mb-4">🎉</div>
          <h2 class="text-2xl font-bold" style="font-family: var(--font-display); color: var(--fg);">
            All Deliveries Complete!
          </h2>
          <p style="color: var(--fg-muted);">
            You've completed {$totalStops} deliveries covering {($totalDistance).toFixed(1)} km
          </p>

          {#if route.endLocation}
            <div class="pt-4">
              <Button
                variant="primary"
                size="lg"
                on:click={handleNavigateToEnd}
              >
                <span class="flex items-center gap-2">
                  <span>Navigate to End Location</span>
                  <span>→</span>
                </span>
              </Button>
            </div>
          {/if}

          <div class="flex gap-3 justify-center pt-4">
            <Button variant="secondary" on:click={handleExportCSV}>
              <span class="flex items-center gap-2">
                <span>Export CSV</span>
              </span>
            </Button>
            <Button variant="ghost" on:click={onNewRoute}>
              <span class="flex items-center gap-2">
                <span>Start New Route</span>
              </span>
            </Button>
          </div>
        </div>
      </Card>
    {/if}

    <!-- Remaining Stops -->
    {#if remaining.length > 0 && !isComplete}
      <div class="animate-slide-up" style="animation-delay: 0.1s;">
        <div class="mb-3 flex items-center gap-2 text-sm" style="color: var(--fg-subtle);">
          <span>⏱</span>
          <span>Upcoming ({remaining.length})</span>
        </div>

        <div class="space-y-2">
          {#each remaining as postcodeIndex, i}
            <Card padding="sm">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold flex-shrink-0" style="background: var(--bg-sunken); color: var(--fg-muted);">
                    {route.currentStopIndex + i + 2}
                  </div>
                  <div class="font-mono font-semibold" style="color: var(--fg);">
                    {route.postcodes[postcodeIndex]}
                  </div>
                </div>

                <button
                  on:click={() => handlePrioritize(postcodeIndex)}
                  class="px-3 py-1 text-xs font-medium rounded-lg transition-colors"
                  style="color: var(--accent);"
                  aria-label="Prioritize {route.postcodes[postcodeIndex]}"
                >
                  Move Up
                </button>
              </div>
            </Card>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Footer Actions -->
    {#if !isComplete}
      <div class="sticky bottom-4 pt-4">
        <Card padding="md" elevated={true}>
          <div class="flex gap-3">
            <Button
              variant="secondary"
              fullWidth={true}
              on:click={handleExportCSV}
            >
              <span class="flex items-center justify-center gap-2">
                <span>📊</span>
                <span>Export CSV</span>
              </span>
            </Button>
            <Button
              variant="ghost"
              fullWidth={true}
              on:click={onNewRoute}
            >
              <span class="flex items-center justify-center gap-2">
                <span>Start New</span>
              </span>
            </Button>
          </div>
        </Card>
      </div>
    {/if}
  </div>
</div>
